import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreathworks } from '@breath-lab/state';
import { BreathworkDifficultyLevel, TrainingBenefit } from '../../../domain/models';

import MediaCard from '@ui/components/MediaCard/MediaCard';
import FilterChip from '@ui/components/FilterChip/FilterChip';
import Button from '@ui/components/Button/Button';
import IconButton from '@ui/components/IconButton/IconButton';
import { chooseBreathworkCardChips } from '../../../domain/logic';
import { breathworkNoBgImageUrl, breathworkNoBgImageSrcSet } from '../../../domain/breathworkHelpers';
import { getPrimaryName, getSecondaryName } from '../../../domain/breathworkNameDisplay';
import { useAuth } from '@breath-lab/state';
import { useBreathworkNameDisplay } from '../../hooks/useBreathworkNameDisplay';
import { breathworkDetailPath } from '../../../routes';
import { useQuickStart } from '../../../domain/quickStartHelpers';
import { useAccessGate } from '../../hooks/useAccessGate';
import { prefetchSubscription } from '@breath-lab/state';
import Modal from '@ui/components/Modal/Modal';
import UpgradePanel from '../../components/UpgradePanel/UpgradePanel';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import { getStaticBreathworkListItems, getStaticBreathworkCards } from '../../../seo/routes';
import { SITE_BASE_URL } from '../../../constants';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import { benefitChipLabel, cardChipLabel } from '../../../domain/breathworkDisplayHelpers';
import styles from './BreathworksPage.module.css';

const MOBILE_QUERY = '(max-width: 640px)';

const COMMON_GOAL_IDS = new Set([
  'STRESS_RELIEF',
  'BETTER_SLEEP',
  'INCREASED_FOCUS_AND_CONCENTRATION',
  'INCREASED_ENERGY_LEVELS',
  'CALMS_BODY_AND_MIND',
  'MOOD_LIFT',
  'EMOTIONAL_BALANCE',
]);

function displayBenefitLabel(b: TrainingBenefit): string {
  return benefitChipLabel(b);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const DIFFICULTY_LEVEL_KEYS: { level: BreathworkDifficultyLevel; key: string }[] = [
  { level: 'BEGINNER', key: 'beginner' },
  { level: 'INTERMEDIATE', key: 'intermediate' },
  { level: 'ADVANCED', key: 'advanced' },
];

export default function BreathworksPage() {
  const { t } = useTranslation('breathworks');
  const isMobile = useIsMobile();
  const { isAuthenticated, user } = useAuth();
  const display = useBreathworkNameDisplay();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const {
    breathworks,
    benefits,
    freeIds,
    paywallEnabled,
    isLoading,
    error,
    query,
    filters,
    filteredBreathworks,
    load,
    loadPublic,
    setQuery,
    toggleBenefit,
    toggleDifficulty,
    clearFilters,
  } = useBreathworks();


  useEffect(() => {
    if (isAuthenticated) {
      load();
    } else {
      loadPublic();
    }
  }, [isAuthenticated, load, loadPublic]);

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'breathworks' });
    logEvent(AnalyticsEvent.viewBreathworks);
  }, []);

  useEffect(() => {
    if (!isFiltersOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFiltersOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isFiltersOpen]);

  const results = filteredBreathworks();
  const visibleBenefits = benefits
    .filter((b) => !b.isFilterHidden)
    .sort((a, b) => a.position - b.position);
  const goalBenefits = visibleBenefits.filter((b) => COMMON_GOAL_IDS.has(b.id));
  const advancedBenefits = visibleBenefits.filter((b) => !COMMON_GOAL_IDS.has(b.id));
  const selectedBenefits = visibleBenefits.filter((b) => filters.selectedBenefitIds.includes(b.id));

  const hasActiveFilters =
    query.trim().length > 0 ||
    filters.selectedBenefitIds.length > 0 ||
    filters.selectedDifficulties.length > 0;
  const activeFilterCount =
    filters.selectedBenefitIds.length +
    filters.selectedDifficulties.length +
    (query.trim().length > 0 ? 1 : 0);
  const totalBreathworks = breathworks.length;
  const resultsSummary = isLoading
    ? t('resultsSummary.loading')
    : hasActiveFilters
      ? t('resultsSummary.filtered', { shown: results.length, total: totalBreathworks })
      : t('resultsSummary.all', { total: totalBreathworks });

  const clearAllAndCloseFilters = () => {
    clearFilters();
    setIsFiltersOpen(false);
  };

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { quickStart, loadingId } = useQuickStart({ onTokenFailed: () => setUpgradeOpen(true) });
  const { isBreathworkAccessible } = useAccessGate();
  const [upgradeLoadingId, setUpgradeLoadingId] = useState<string | null>(null);

  const STARTER_IDS = ['SAMA_VRITTI', '4_7_8', 'UJJAYI'] as const;
  const starterIdSet = new Set<string>(STARTER_IDS);
  const starterBreathworks = STARTER_IDS
    .map((id) => breathworks.find((b) => b.id === id))
    .filter((b): b is NonNullable<typeof b> => b != null);
  const showStarterSection = !isLoading && !hasActiveFilters && starterBreathworks.length > 0;
  const gridResults = showStarterSection
    ? results.filter((b) => !starterIdSet.has(b.id))
    : results;

  const renderCard = (b: typeof breathworks[0]) => {
    const isPro = paywallEnabled && !freeIds.includes(b.id);
    const badge: { label: string; variant: 'premium' } | undefined = isAuthenticated && paywallEnabled && isPro
      ? { label: t('badges.pro'), variant: 'premium' }
      : undefined;
    const { chips } = chooseBreathworkCardChips(b, benefits);
    const tags = chips.map((c) => { const l = cardChipLabel(c); return c.emoji ? `${c.emoji} ${l}` : l; });
    return (
      <li key={b.id}>
        <MediaCard
          image={breathworkNoBgImageUrl(b.id) ?? undefined}
          imageSrcSet={breathworkNoBgImageSrcSet(b.id) ?? undefined}
          imageAlt={b.name.en}
          imageContain
          flairColor={b.backgroundColor}
          badge={badge}
          title={getPrimaryName(b.name, display)}
          subtitle={getSecondaryName(b.name, display)}
          tags={tags}
          href={breathworkDetailPath(b.id)}
          onQuickStart={isAuthenticated ? async () => {
            if (!isBreathworkAccessible(b.id)) {
              logEvent(AnalyticsEvent.premiumGateShown, { [AnalyticsParam.breathworkId]: b.id });
              setUpgradeLoadingId(b.id);
              await prefetchSubscription(user!.uid);
              setUpgradeLoadingId(null);
              setUpgradeOpen(true);
              return;
            }
            logEvent(AnalyticsEvent.quickStartTapped, { [AnalyticsParam.breathworkId]: b.id });
            quickStart(b);
          } : undefined}
          quickStartLoading={loadingId === b.id || upgradeLoadingId === b.id}
        />
      </li>
    );
  };

  return (
    <div className={styles.page}>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        path="/breathworks"
        image={`${SITE_BASE_URL}/og-breathworks.png`}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Pranayama Techniques',
          numberOfItems: getStaticBreathworkListItems().length,
          itemListElement: getStaticBreathworkListItems(),
        }}
      />
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTagline}>{t('pageTagline')}</h1>
        <p className={styles.pageDesc}>{t('pageDesc')}</p>
      </div>

      <section className={styles.controlsPanel} aria-label="Search and filters">
        <div className={styles.searchRow}>
          <div className={styles.searchWrapper}>
            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className={styles.searchInput}
              type="search"
              placeholder={isMobile ? t('searchPlaceholderShort') : t('searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onBlur={() => {
                if (query.trim().length > 0) {
                  logEvent(AnalyticsEvent.search, { [AnalyticsParam.query]: query.trim() });
                }
              }}
              aria-label={t('searchAriaLabel')}
            />
          </div>
          <Button
            variant="secondary"
            className={styles.filtersButton}
            onClick={() => {
              logEvent(AnalyticsEvent.filtersOpened);
              setIsFiltersOpen(true);
            }}
            aria-label={hasActiveFilters ? t('openFiltersActive', { count: activeFilterCount }) : t('openFilters')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span className={styles.filtersButtonLabel}>{t('filtersButton')}</span>
            {hasActiveFilters && <span className={styles.filterCount}>{activeFilterCount}</span>}
          </Button>
        </div>

        {hasActiveFilters && (
          <div className={styles.activeFilters} aria-label="Active filters">
            <div className={styles.activeFiltersHeader}>
              <span className={styles.activeFiltersLabel}>{t('activeFilters')}</span>
              <Button variant="ghost" onClick={clearFilters}>
                {t('clearAll')}
              </Button>
            </div>
            <div className={styles.activeFilterList}>
              {query.trim().length > 0 && (
                <FilterChip
                  variant="removable"
                  onClick={() => setQuery('')}
                  ariaLabel={`Remove search query ${query.trim()}`}
                  prefix={t('filterChipPrefix.search')}
                  suffix="×"
                >
                  {query.trim()}
                </FilterChip>
              )}

              {filters.selectedDifficulties.map((level) => {
                const key = DIFFICULTY_LEVEL_KEYS.find((d) => d.level === level)?.key ?? level.toLowerCase();
                const label = t(`difficulty.${key}` as any) as string;
                return (
                  <FilterChip
                    key={level}
                    variant="removable"
                    onClick={() => toggleDifficulty(level)}
                    ariaLabel={`Remove difficulty filter ${label}`}
                    prefix={t('filterChipPrefix.difficulty')}
                    suffix="×"
                  >
                    {label}
                  </FilterChip>
                );
              })}

              {selectedBenefits.map((benefit) => (
                <FilterChip
                  key={benefit.id}
                  variant="removable"
                  onClick={() => toggleBenefit(benefit.id)}
                  ariaLabel={`Remove goal filter ${displayBenefitLabel(benefit)}`}
                  prefix={t('filterChipPrefix.goal')}
                  leading={benefit.emoji ? <span>{benefit.emoji}</span> : undefined}
                  suffix="×"
                >
                  {displayBenefitLabel(benefit)}
                </FilterChip>
              ))}
            </div>
          </div>
        )}
      </section>

      {isFiltersOpen && (
        <div
          className={styles.filterOverlay}
          role="presentation"
          onClick={() => setIsFiltersOpen(false)}
        >
          <div
            className={styles.filterSheet}
            role="dialog"
            aria-modal="true"
            aria-labelledby="breathworks-filters-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.filterSheetHeader}>
              <div className={styles.filterSheetCopy}>
                <h2 id="breathworks-filters-title" className={styles.filterSheetTitle}>{t('filtersTitle')}</h2>
              </div>
              <IconButton
                variant="bordered"
                label={t('closeFilters')}
                className={styles.closeFiltersBtn}
                onClick={() => setIsFiltersOpen(false)}
              >
                <span aria-hidden="true">×</span>
              </IconButton>
            </div>

            <div className={styles.filtersGrid}>
              <div className={styles.filterSection}>
                <span className={styles.filterLabel}>{t('filterSections.difficulty')}</span>
                <div className={styles.chips}>
                  {DIFFICULTY_LEVEL_KEYS.map(({ level, key }) => (
                    <FilterChip
                      key={level}
                      onClick={() => toggleDifficulty(level)}
                      pressed={filters.selectedDifficulties.includes(level)}
                      isActive={filters.selectedDifficulties.includes(level)}
                    >
                      {t(`difficulty.${key}` as any)}
                    </FilterChip>
                  ))}
                </div>
              </div>

              {goalBenefits.length > 0 && (
                <div className={styles.filterSection}>
                  <span className={styles.filterLabel}>{t('filterSections.goal')}</span>
                  <div className={styles.chips}>
                    {goalBenefits.map((b) => (
                      <FilterChip
                        key={b.id}
                        onClick={() => toggleBenefit(b.id)}
                        pressed={filters.selectedBenefitIds.includes(b.id)}
                        isActive={filters.selectedBenefitIds.includes(b.id)}
                        leading={b.emoji ? <span>{b.emoji}</span> : undefined}
                      >
                        {displayBenefitLabel(b)}
                      </FilterChip>
                    ))}
                  </div>
                </div>
              )}

              {advancedBenefits.length > 0 && (
                <div className={styles.filterSection}>
                  <span className={styles.filterLabel}>{t('filterSections.advanced')}</span>
                  <div className={styles.chips}>
                    {advancedBenefits.map((b) => (
                      <FilterChip
                        key={b.id}
                        onClick={() => toggleBenefit(b.id)}
                        pressed={filters.selectedBenefitIds.includes(b.id)}
                        isActive={filters.selectedBenefitIds.includes(b.id)}
                        leading={b.emoji ? <span>{b.emoji}</span> : undefined}
                      >
                        {displayBenefitLabel(b)}
                      </FilterChip>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.filterSheetFooter}>
              <Button
                variant="secondary"
                className={styles.footerBtn}
                onClick={clearAllAndCloseFilters}
                disabled={!hasActiveFilters}
              >
                {t('clearAll')}
              </Button>
              <Button
                variant="primary"
                className={styles.footerBtn}
                onClick={() => {
                  logEvent(AnalyticsEvent.filtersApplied, { filter_count: activeFilterCount });
                  setIsFiltersOpen(false);
                }}
              >
                {t('showResults', { count: results.length })}
              </Button>
            </div>
          </div>
        </div>
      )}

      <p className={styles.resultsMeta} aria-live="polite">{resultsSummary}</p>

      {/* Starter section — hidden when search or filters are active */}
      {showStarterSection && (
        <section className={styles.starterSection} aria-label={t('starterSection.ariaLabel')}>
          <h2 className={styles.starterHeader}>{t('starterSection.heading')}</h2>
          <ul className={styles.starterGrid}>
            {starterBreathworks.map(renderCard)}
          </ul>
        </section>
      )}

      {/* Full library grid */}
      {error ? (
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>{t('errors.couldNotLoad')}</p>
          <Button variant="secondary" onClick={load}>
            {t('retry', { ns: 'common' })}
          </Button>
        </div>
      ) : isLoading ? (
        <ul className={styles.grid}>
          {getStaticBreathworkCards().map((item) => (
            <li key={item.path}>
              <a href={item.path} className={styles.skeletonCardLink} aria-label={item.name}>
                <span className={styles.skeletonCardName}>{item.name}</span>
                <span className={styles.skeletonCardDesc}>{item.description}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : results.length === 0 ? (
        <div className={styles.empty}>
          <p>{t('empty')}</p>
          {hasActiveFilters && (
            <Button variant="secondary" onClick={clearFilters}>
              {t('clearAll')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {!hasActiveFilters && (
            <h2 className={styles.libraryHeader}>{t('allTechniques')}</h2>
          )}
          <ul className={styles.grid}>
            {gridResults.map(renderCard)}
          </ul>
        </>
      )}
      {upgradeOpen && (
        <Modal onClose={() => setUpgradeOpen(false)} wide>
          <UpgradePanel onComplete={() => setUpgradeOpen(false)} />
        </Modal>
      )}
    </div>
  );
}
